import React, {
  useCallback,
  useEffect,
  useState,
  useMemo,
  useRef,
} from "react";
import { useSelector } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import axios from "axios";
import {
  Search,
  ChevronDown,
  ChevronRight,
  AlertCircle,
  Calendar,
  User,
  Hash,
  Plus,
  FlaskConical,
  Scale,
  RotateCcw,
  History,
  XCircle,
  Archive,
  Layers,
  PackageCheck,
  Tag,
  Warehouse,
  Edit,
  CheckCircle2,
  Clock,
  Eye,
  Weight,
  BarChart3,
  CircleDashed,
  TriangleAlert,
  Loader2,
  Printer, // <-- Added Printer icon
} from "lucide-react";
import Loader from "../../loader/Loader";
import Pagination from "../Pagination";
import { domain } from "../../../security";
import { selectUserID } from "../../../redux/IchthusSlice";
import AddProduction from "./AddProduction";
import { useReactToPrint } from "react-to-print";
import { ProductionRecordPrint, ProductionListPrint } from "./ProductionPrint"; // Adjust path if needed

// ─── COLOR MAP ────────────────────────────────────────────────────────────────
const colorMap = {
  blue: {
    bg: "bg-blue-500",
    bgLight: "bg-blue-50",
    text: "text-blue-600",
    border: "border-blue-200",
  },
  amber: {
    bg: "bg-amber-500",
    bgLight: "bg-amber-50",
    text: "text-amber-600",
    border: "border-amber-200",
  },
  slate: {
    bg: "bg-slate-500",
    bgLight: "bg-slate-50",
    text: "text-slate-600",
    border: "border-slate-200",
  },
  emerald: {
    bg: "bg-emerald-500",
    bgLight: "bg-emerald-50",
    text: "text-emerald-600",
    border: "border-emerald-200",
  },
  violet: {
    bg: "bg-violet-500",
    bgLight: "bg-violet-50",
    text: "text-violet-600",
    border: "border-violet-200",
  },
  rose: {
    bg: "bg-rose-500",
    bgLight: "bg-rose-50",
    text: "text-rose-600",
    border: "border-rose-200",
  },
  yellow: {
    bg: "bg-yellow-500",
    bgLight: "bg-yellow-50",
    text: "text-yellow-600",
    border: "border-yellow-200",
  },
  orange: {
    bg: "bg-orange-500",
    bgLight: "bg-orange-50",
    text: "text-orange-600",
    border: "border-orange-200",
  },
  cyan: {
    bg: "bg-cyan-500",
    bgLight: "bg-cyan-50",
    text: "text-cyan-600",
    border: "border-cyan-200",
  },
};

// ─── FORMATTERS ───────────────────────────────────────────────────────────────
const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};
const fmtQty = (val, unit = "kg") =>
  val != null && val !== ""
    ? `${new Intl.NumberFormat("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(val)} ${unit}`
    : "—";
const fmt = (val) =>
  new Intl.NumberFormat("en-PH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(val ?? 0);

// ─── STATUS BADGE ─────────────────────────────────────────────────────────────
const MeasurementBadge = ({ hasActualMeasurements }) =>
  hasActualMeasurements ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200">
      <CheckCircle2 size={9} /> Measured
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider bg-amber-50 text-amber-700 border border-amber-200">
      <Clock size={9} /> Pending
    </span>
  );

// ─── MEASUREMENTS VIEW MODAL ──────────────────────────────────────────────────
const MeasurementsViewModal = ({ record, isOpen, onClose }) => {
  const [actualData, setActualData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && record?.hasActualMeasurements) {
      let isMounted = true;
      setLoading(true);
      axios
        .get(`${domain}/api/ActualMeasurements/by-production/${record.id}`)
        .then((res) => {
          if (isMounted) setActualData(res.data);
        })
        .catch(() => {
          if (isMounted) toast.error("Failed to load actual measurements.");
        })
        .finally(() => {
          if (isMounted) setLoading(false);
        });

      return () => {
        isMounted = false;
      };
    } else {
      setActualData(null);
    }
  }, [isOpen, record]);

  if (!isOpen || !record) return null;

  const isActual = record.hasActualMeasurements;
  const entries = isActual ? actualData?.entries || [] : record.entries || [];
  const totalWeight = isActual
    ? (actualData?.totalActualWeight ?? 0)
    : (record.totalActualWeight ?? 0);
  const remarks = isActual
    ? actualData?.remarks || record.remarks
    : record.remarks;

  const grouped = entries.reduce((acc, e) => {
    const key = e.groupName || "Materials";
    if (!acc[key]) acc[key] = [];
    acc[key].push(e);
    return acc;
  }, {});

  return (
    <div className="fixed inset-0 z-[85] flex items-center justify-center bg-slate-900/70 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div
          className={`px-6 py-4 border-b border-slate-100 flex items-center justify-between shrink-0 ${isActual ? "bg-emerald-50/50" : "bg-slate-50/50"}`}
        >
          <div className="flex items-center gap-3">
            <div
              className={`p-2.5 bg-white shadow-sm rounded-xl border ${isActual ? "border-emerald-200" : "border-slate-200"}`}
            >
              <BarChart3
                size={18}
                className={isActual ? "text-emerald-600" : "text-slate-600"}
              />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-base font-bold text-slate-800 leading-tight">
                  {isActual ? "Actual Measurements" : "Planned Entries"}
                </h2>
                <MeasurementBadge hasActualMeasurements={isActual} />
              </div>
              <p
                className={`text-[11px] font-bold tracking-widest uppercase mt-0.5 ${isActual ? "text-emerald-500" : "text-slate-400"}`}
              >
                {record.productionNumber || `PRD-${record.id}`}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition"
          >
            <XCircle size={22} />
          </button>
        </div>

        {/* Meta strip */}
        <div className="px-6 py-3 bg-slate-50 border-b border-slate-100 flex flex-wrap gap-4 shrink-0">
          <div className="flex items-center gap-1.5 text-xs text-slate-600 font-semibold">
            <PackageCheck size={13} className="text-indigo-400" />
            DO:{" "}
            <span className="font-bold text-indigo-700 ml-1">
              {record.deliveryOrderNumber || "—"}
            </span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-slate-600 font-semibold">
            <Calendar size={13} className="text-slate-400" />{" "}
            {formatDate(record.productionDate)}
          </div>
          <div className="flex items-center gap-1.5 text-xs text-slate-600 font-semibold">
            <Warehouse size={13} className="text-slate-400" />{" "}
            {record.warehouseName || "—"}
          </div>
        </div>

        {/* Pending warning */}
        {!isActual && (
          <div className="px-6 py-3 bg-amber-50 border-b border-amber-100 flex items-center gap-2.5 shrink-0">
            <TriangleAlert size={15} className="text-amber-500 shrink-0" />
            <p className="text-xs font-semibold text-amber-700">
              Showing <strong>planned</strong> entries from Step 1. Actual
              weigh-in has not been submitted yet. Use{" "}
              <strong>Actual Measurements</strong> to record the real
              quantities.
            </p>
          </div>
        )}

        {/* Body */}
        {loading ? (
          <div className="flex-1 flex flex-col items-center justify-center py-20 bg-slate-50/50">
            <Loader2 size={36} className="animate-spin text-emerald-500 mb-3" />
            <p className="text-sm font-bold text-slate-500">
              Loading actual measurements...
            </p>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto p-6 bg-slate-50/50 space-y-5">
            {entries.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-16 h-16 bg-white border border-slate-200 rounded-full flex items-center justify-center mb-3 shadow-sm">
                  <CircleDashed size={26} className="text-slate-300" />
                </div>
                <p className="text-slate-500 font-bold">
                  No material entries recorded yet.
                </p>
              </div>
            ) : (
              Object.entries(grouped).map(([groupName, items]) => (
                <div
                  key={groupName}
                  className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden"
                >
                  <div className="px-4 py-3 bg-indigo-50/50 border-b border-slate-100 flex items-center gap-2">
                    <Layers size={13} className="text-indigo-400" />
                    <h4 className="text-[10px] font-black uppercase tracking-widest text-indigo-700">
                      {groupName}
                    </h4>
                  </div>
                  <div className="divide-y divide-slate-50">
                    {items.map((entry, idx) => {
                      const c = colorMap[entry.color] || colorMap.slate;
                      const pct =
                        totalWeight > 0
                          ? Math.min(
                              100,
                              (parseFloat(entry.quantity) / totalWeight) * 100,
                            )
                          : 0;
                      return (
                        <div
                          key={idx}
                          className="px-4 py-3.5 hover:bg-slate-50 transition-colors"
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2 min-w-0">
                              <span
                                className={`shrink-0 px-2 py-0.5 rounded-md text-[10px] font-black uppercase tracking-wider border ${c.border} ${c.bgLight} ${c.text}`}
                              >
                                {entry.tagName}
                              </span>
                              <span
                                className="text-xs text-slate-600 font-medium truncate"
                                title={entry.productName || "No Product"}
                              >
                                {entry.productName || "—"}
                              </span>
                            </div>
                            <span
                              className={`text-sm font-black ${c.text} shrink-0 ml-3`}
                            >
                              {fmtQty(entry.quantity, entry.uomName)}
                            </span>
                          </div>

                          {(entry.uomName?.toLowerCase() === "kg" ||
                            entry.uomName?.toLowerCase() === "g") && (
                            <div className="space-y-0.5 mt-1">
                              <div className="flex justify-between text-[9px] font-bold text-slate-400 uppercase tracking-wide">
                                <span>Share of batch</span>
                                <span>{pct.toFixed(1)}%</span>
                              </div>
                              <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                <div
                                  className={`h-full ${c.bg} rounded-full transition-all duration-500`}
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))
            )}

            {/* Total weight summary */}
            {totalWeight > 0 && (
              <div
                className={`p-4 rounded-xl border flex items-center justify-between shadow-sm ${isActual ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-200"}`}
              >
                <div
                  className={`flex items-center gap-2.5 ${isActual ? "text-emerald-700" : "text-slate-600"}`}
                >
                  <Weight size={18} />
                  <span className="text-xs font-black uppercase tracking-widest">
                    {isActual ? `Total Actual ${record?.productName}` : `Total Planned ${record?.productName}`}
                  </span>
                </div>
                <span
                  className={`text-2xl font-black ${isActual ? "text-emerald-700" : "text-slate-700"}`}
                >
                  {fmt(totalWeight)}{" "}
                  <span
                    className={`text-sm font-bold ${isActual ? "text-emerald-500" : "text-slate-400"}`}
                  >
                    {`${record?.uomName ? record?.uomName : ""}`}
                  </span>
                </span>
              </div>
            )}

            {/* Remarks */}
            {remarks && (
              <div className="p-4 bg-amber-50 rounded-xl border border-amber-100 shadow-sm">
                <p className="text-[10px] font-black uppercase tracking-widest text-amber-600 mb-1.5">
                  Remarks
                </p>
                <p className="text-sm text-amber-900 font-medium">{remarks}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── HISTORY MODAL ────────────────────────────────────────────────────────────
const ProductionHistoryModal = ({
  isOpen,
  onClose,
  currentUserId,
  onRestored,
}) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  const fetchHistory = useCallback(async () => {
    if (!isOpen) return;
    setLoading(true);
    try {
      const res = await axios.get(`${domain}/api/Productions/history`);
      setHistory(res.data || []);
    } catch {
      toast.error("Failed to load production history.");
    } finally {
      setLoading(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      fetchHistory();
      setCurrentPage(1);
    }
  }, [isOpen, fetchHistory]);

  const handleRestore = async (id) => {
    try {
      await axios.put(`${domain}/api/Productions/restore/${id}`, {
        userID: currentUserId,
      });
      toast.success("Production record restored!");
      fetchHistory();
      onRestored();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to restore.");
    }
  };

  if (!isOpen) return null;
  const filtered = history.filter((r) => {
    const q = search.toLowerCase();
    return (
      (r.productionNumber || "").toLowerCase().includes(q) ||
      (r.deliveryOrderNumber || "").toLowerCase().includes(q)
    );
  });
  const currentItems = filtered.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[88vh] flex flex-col overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between shrink-0 bg-slate-50/50">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-white border border-slate-200 shadow-sm rounded-xl">
              <History size={18} className="text-slate-600" />
            </div>
            <div>
              <h2 className="text-base font-bold text-slate-800 leading-tight">
                Archived Production Records
              </h2>
              <p className="text-[11px] font-bold tracking-widest text-slate-400 uppercase mt-0.5">
                Restore soft-deleted records
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition"
          >
            <XCircle size={22} />
          </button>
        </div>
        <div className="px-6 py-4 border-b border-slate-100 shrink-0">
          <div className="relative">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search by production number or DO reference..."
              className="w-full pl-10 pr-4 py-2.5 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-emerald-200 focus:border-emerald-400 outline-none transition-all"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-6 bg-slate-50/50">
          {loading ? (
            <div className="flex justify-center py-16">
              <Loader />
            </div>
          ) : currentItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-14 h-14 bg-white border border-slate-200 shadow-sm rounded-full flex items-center justify-center mb-3">
                <Archive size={22} className="text-slate-400" />
              </div>
              <p className="text-slate-500 font-medium">
                No archived records found.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {currentItems.map((item) => (
                <div
                  key={item.id}
                  className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 hover:border-emerald-200 transition-colors"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <div className="p-1.5 bg-slate-50 border border-slate-100 rounded-lg text-slate-500 shrink-0">
                          <History size={14} />
                        </div>
                        <p className="font-bold text-slate-800 text-sm truncate">
                          {item.productionNumber || `PRD-${item.id}`}
                        </p>
                        <MeasurementBadge
                          hasActualMeasurements={item.hasActualMeasurements}
                        />
                      </div>
                      <p className="text-xs text-indigo-600 font-semibold mb-2">
                        DO: {item.deliveryOrderNumber || "—"}
                      </p>
                      <div className="flex flex-wrap items-center gap-3 text-[11px] text-slate-500 font-medium mt-2">
                        <span className="flex items-center gap-1.5 bg-slate-50 px-2 py-1 rounded-md border border-slate-100">
                          <Calendar size={12} className="text-slate-400" />{" "}
                          {formatDate(item.productionDate)}
                        </span>
                        <span className="flex items-center gap-1.5 bg-emerald-50 text-emerald-700 px-2 py-1 rounded-md border border-emerald-100">
                          <Scale size={12} />{" "}
                          {fmtQty(item.totalActualWeight, "kg")}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => handleRestore(item.id)}
                      className="flex items-center gap-1.5 px-3 py-2 bg-white text-emerald-600 border border-emerald-200 rounded-xl text-xs font-bold hover:bg-emerald-50 hover:border-emerald-300 shadow-sm transition shrink-0"
                    >
                      <RotateCcw size={14} /> Restore
                    </button>
                  </div>
                </div>
              ))}
              <div className="pt-4">
                <Pagination
                  itemsPerPage={itemsPerPage}
                  totalItems={filtered.length}
                  currentPage={currentPage}
                  paginate={(p) => setCurrentPage(p)}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
const AllProduction = () => {
  const currentUserId = useSelector(selectUserID);

  const [productionData, setProductionData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedToEdit, setSelectedToEdit] = useState(null);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [measurementsRecord, setMeasurementsRecord] = useState(null);

  // Single Print Setup
  const singlePrintRef = useRef(null);
  const [printTarget, setPrintTarget] = useState(null);
  const handleSinglePrint = useReactToPrint({
    content: () => singlePrintRef.current,
  });

  // Fetch actual data if record is measured before triggering print
  const triggerSinglePrint = async (item) => {
    let actualData = null;
    if (item.hasActualMeasurements) {
      const toastId = toast.loading("Preparing print document...");
      try {
        const res = await axios.get(
          `${domain}/api/ActualMeasurements/by-production/${item.id}`,
        );
        actualData = res.data;
        toast.dismiss(toastId);
      } catch (err) {
        toast.update(toastId, {
          render: "Failed to load actual measurements.",
          type: "error",
          isLoading: false,
          autoClose: 3000,
        });
        return;
      }
    }

    setPrintTarget({ data: item, actualData });
    setTimeout(() => handleSinglePrint(), 200);
  };

  // General Report Print Setup
  const listPrintRef = useRef(null);
  const handleListPrint = useReactToPrint({
    content: () => listPrintRef.current,
  });

  const triggerListPrint = () => {
    setTimeout(() => handleListPrint(), 200);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${domain}/api/Productions`);
      setProductionData((res.data || []).sort((a, b) => b.id - a.id));
    } catch {
      toast.error("Failed to fetch production records.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSoftDelete = async (id) => {
    if (
      !window.confirm(
        "Archive this production record? It can be restored later.",
      )
    )
      return;
    try {
      await axios.put(`${domain}/api/Productions/soft-delete/${id}`, {
        userID: currentUserId,
      });
      toast.success("Production record archived.");
      if (expandedId === id) setExpandedId(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to archive.");
    }
  };

  const handleEdit = (record) => {
    const _entries = (record.entries || []).reduce((acc, item) => {
      acc[item.productionTaggingId] = {
        quantity: item.quantity?.toString() || "",
        productId: item.productId ?? null,
        productItemCode: item.productItemCode ?? null,
        productName: item.productName ?? null,
        brandId: item.brandId ?? null,
        brandName: item?.brandName ?? "",
        uomId: item.uomId ?? null,
        uomName: item.uomName ?? null,
      };
      return acc;
    }, {});

    setSelectedToEdit({
      ...record,
      entries: _entries
    });
    
    setIsEditModalOpen(true);
  };

  // ─── STATS ────────────────────────────────────────────────────────────────
  const materialStats = useMemo(() => {
    const stats = {};
    productionData.forEach((prod) => {
      (prod.entries || []).forEach((e) => {
        if (!stats[e.productName])
          stats[e.productName] = { total: 0, color: e.color, unit: e.unit };
        stats[e.productName].total += parseFloat(e.quantity) || 0;
      });
    });
    return Object.entries(stats)
      .sort((a, b) => b[1].total - a[1].total)
      .slice(0, 4)
      .map(([name, data]) => ({ name, ...data }));
  }, [productionData]);

  const summaryStats = useMemo(() => {
    const total = productionData.length;
    const measured = productionData.filter(
      (r) => r.hasActualMeasurements,
    ).length;
    return { total, measured, pending: total - measured };
  }, [productionData]);

  const filteredData = productionData.filter((r) => {
    const q = searchTerm.toLowerCase();
    return (
      (r.productionNumber || "").toLowerCase().includes(q) ||
      (r.deliveryOrderNumber || "").toLowerCase().includes(q) ||
      (r.remarks || "").toLowerCase().includes(q)
    );
  });
  const currentRecords = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );
  const toggleExpand = (id) => setExpandedId(expandedId === id ? null : id);

  return (
    <div className="min-h-screen pb-12 bg-slate-50/50">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* PAGE HEADER */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-30 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-emerald-50 border border-emerald-100 rounded-2xl shadow-sm">
                <FlaskConical size={24} className="text-emerald-600" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-800 leading-tight">
                  Production Records
                </h1>
                <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">
                  Dynamic Batch Tracking & Formulation
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={triggerListPrint}
                className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-blue-600 rounded-xl text-sm font-bold transition-all shadow-sm"
              >
                <Printer size={16} />{" "}
                <span className="hidden sm:inline">Print Summary</span>
              </button>
              <button
                onClick={() => setIsHistoryModalOpen(true)}
                className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-indigo-600 rounded-xl text-sm font-bold transition-all shadow-sm"
              >
                <History size={16} />{" "}
                <span className="hidden sm:inline">Archived</span>
              </button>
              <button
                onClick={() => setIsAddModalOpen(true)}
                className="flex items-center gap-2 px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-sm font-black transition-all shadow-md hover:shadow-lg active:scale-95"
              >
                <Plus size={18} /> New Production
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
        {/* ── SUMMARY STRIP ─────────────────────────────────────────────────── */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          {[
            {
              label: "Total",
              value: summaryStats.total,
              icon: <FlaskConical size={18} className="text-slate-600" />,
              bg: "bg-slate-50 border-slate-100",
              text: "text-slate-800",
              sub: "text-slate-400",
            },
            {
              label: "Measured",
              value: summaryStats.measured,
              icon: <CheckCircle2 size={18} className="text-emerald-600" />,
              bg: "bg-emerald-50 border-emerald-100",
              text: "text-emerald-700",
              sub: "text-emerald-500",
            },
            {
              label: "Pending",
              value: summaryStats.pending,
              icon: <Clock size={18} className="text-amber-600" />,
              bg: "bg-amber-50 border-amber-100",
              text: "text-amber-700",
              sub: "text-amber-500",
            },
          ].map(({ label, value, icon, bg, text, sub }) => (
            <div
              key={label}
              className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm flex items-center gap-3"
            >
              <div className={`p-2.5 border rounded-xl ${bg}`}>{icon}</div>
              <div>
                <p className={`text-2xl font-black leading-none ${text}`}>
                  {value}
                </p>
                <p
                  className={`text-[10px] font-black uppercase tracking-widest mt-0.5 ${sub}`}
                >
                  {label}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* ── MATERIAL STATS ────────────────────────────────────────────────── */}
        {materialStats.length > 0 && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {materialStats.map((mat, idx) => {
              const colors = colorMap[mat.color] || colorMap.slate;
              return (
                <div
                  key={idx}
                  className={`rounded-2xl border ${colors.border} bg-white p-4 flex flex-col justify-between shadow-sm relative overflow-hidden group`}
                >
                  <div className="flex items-start justify-between mb-3 z-10">
                    <p
                      className={`text-[10px] font-black uppercase tracking-widest ${colors.text} opacity-80`}
                    >
                      Overall {mat.name}
                    </p>
                    <Tag size={14} className={colors.text} />
                  </div>
                  <div className="z-10">
                    <p
                      className={`text-2xl font-black ${colors.text} tracking-tight`}
                    >
                      {new Intl.NumberFormat("en-PH", {
                        maximumFractionDigits: 0,
                      }).format(mat.total)}
                      <span className="text-[11px] font-bold ml-1.5 opacity-70 uppercase tracking-widest">
                        {mat.unit}
                      </span>
                    </p>
                  </div>
                  <div
                    className={`absolute inset-0 opacity-0 group-hover:opacity-100 ${colors.bgLight} transition-opacity duration-500`}
                  />
                  <Tag
                    size={72}
                    className={`absolute -bottom-4 -right-4 opacity-[0.04] ${colors.text} transform group-hover:scale-110 transition-transform duration-500`}
                  />
                </div>
              );
            })}
          </div>
        )}

        {/* ── SEARCH BAR ────────────────────────────────────────────────────── */}
        <div className="bg-white p-2 border border-slate-200 rounded-2xl shadow-sm mb-6 flex">
          <div className="relative flex-grow">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <input
              type="text"
              placeholder="Search by production #, DO reference, or remarks..."
              className="w-full pl-12 pr-4 py-3 bg-transparent rounded-xl focus:outline-none text-sm font-medium text-slate-800 placeholder-slate-400"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>
        </div>

        {/* ── TABLE / CARDS ──────────────────────────────────────────────────── */}
        {loading ? (
          <div className="flex justify-center items-center py-24">
            <Loader />
          </div>
        ) : (
          <>
            {/* DESKTOP TABLE */}
            <div className="hidden lg:block bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
              <table className="w-full text-sm text-left text-slate-600">
                <thead className="bg-slate-50/50 border-b border-slate-200 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                  <tr>
                    <th className="w-12 px-5 py-4"></th>
                    <th className="px-5 py-4">Production #</th>
                    <th className="px-5 py-4">Ref. Order</th>
                    <th className="px-5 py-4">Date</th>
                    <th className="px-5 py-4">Status</th>
                    <th className="px-5 py-4 min-w-[200px] hidden">Mix Composition</th>
                    <th className="px-5 py-4 text-right">Actual</th>
                    <th className="px-5 py-4 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {currentRecords.map((record) => (
                    <React.Fragment key={record.id}>
                      <tr
                        onClick={() => toggleExpand(record.id)}
                        className={`hover:bg-slate-50 transition cursor-pointer group ${expandedId === record.id ? "bg-slate-50" : ""}`}
                      >
                        <td className="px-5 py-4 text-center text-slate-300 group-hover:text-slate-500">
                          {expandedId === record.id ? (
                            <ChevronDown size={16} />
                          ) : (
                            <ChevronRight size={16} />
                          )}
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-2.5">
                            <div className="p-1.5 bg-emerald-50 border border-emerald-100 rounded-lg">
                              <FlaskConical
                                size={14}
                                className="text-emerald-600"
                              />
                            </div>
                            <span className="font-bold text-slate-800">
                              {record.productionNumber || `PRD-${record.id}`}
                            </span>
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          <span className="inline-flex items-center gap-1.5 text-[10px] font-bold text-indigo-700 bg-indigo-50 border border-indigo-100 px-2 py-1 rounded-md tracking-wider">
                            <PackageCheck size={12} />{" "}
                            {record.deliveryOrderNumber || "—"}
                          </span>
                        </td>
                        <td className="px-5 py-4 text-slate-500 font-medium text-xs">
                          {formatDate(record.productionDate)}
                        </td>
                        <td className="px-5 py-4">
                          <MeasurementBadge
                            hasActualMeasurements={record.hasActualMeasurements}
                          />
                        </td>
                        <td className="px-5 py-4 hidden">
                          <div className="flex flex-wrap gap-1.5">
                            {record.entries?.slice(0, 3).map((e) => {
                              const c = colorMap[e.color] || colorMap.slate;
                              return (
                                <span
                                  key={e.id}
                                  className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-md border ${c.border} ${c.bgLight} ${c.text}`}
                                >
                                  {e.tagName}
                                </span>
                              );
                            })}
                            {record.entries?.length > 3 && (
                              <span className="inline-flex items-center text-[10px] font-bold px-2 py-0.5 rounded-md border border-slate-200 bg-slate-50 text-slate-500">
                                +{record.entries.length - 3} more
                              </span>
                            )}
                            {(!record.entries ||
                              record.entries.length === 0) && (
                              <span className="text-xs text-slate-400 italic">
                                No formulation data
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-5 py-4 text-right">
                          <span className="font-black text-emerald-700">
                            {fmtQty(record.totalActualWeight, record.uomName ?? "")}
                          </span>
                        </td>
                        <td className="px-5 py-4">
                          <div
                            className="flex items-center justify-center gap-2"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button
                              onClick={() => triggerSinglePrint(record)}
                              className="px-3 py-1.5 text-[11px] font-bold text-blue-600 bg-white border border-blue-200 rounded-lg hover:bg-blue-50 transition shadow-sm flex items-center gap-1"
                            >
                              <Printer size={12} /> Print
                            </button>
                            { !record.hasActualMeasurements &&
                              <>
                                <button
                                  onClick={() => handleEdit(record)}
                                  className="px-3 py-1.5 text-[11px] font-bold text-amber-700 bg-white border border-amber-200 rounded-lg hover:bg-amber-50 transition shadow-sm flex items-center gap-1"
                                >
                                  <Edit size={12} /> Edit
                                </button>
                                <button
                                  onClick={() => handleSoftDelete(record.id)}
                                  className="px-3 py-1.5 text-[11px] font-bold text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50 transition shadow-sm flex items-center gap-1"
                                >
                                  <Archive size={12} /> Archive
                                </button>
                              </>
                            }
                          </div>
                        </td>
                      </tr>

                      {/* ── EXPANDED ROW ── */}
                      {expandedId === record.id && (
                        <tr>
                          <td
                            colSpan="8"
                            className="bg-slate-50/80 px-8 py-6 border-b border-slate-200 shadow-inner"
                          >
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                              <div>
                                <div className="flex items-center justify-between mb-4">
                                  <h4 className="text-[10px] font-black uppercase tracking-widest text-slate-400 flex items-center gap-2">
                                    <Layers
                                      size={14}
                                      className="text-indigo-400"
                                    />
                                    {record.hasActualMeasurements
                                      ? "Actual Material Consumption"
                                      : "Planned Mix Breakdown"}
                                  </h4>
                                  <button
                                    onClick={() =>
                                      setMeasurementsRecord(record)
                                    }
                                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold border shadow-sm transition
                                      ${record.hasActualMeasurements ? "bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700" : "bg-white text-amber-600 border-amber-200 hover:bg-amber-50"}`}
                                  >
                                    <Eye size={12} />
                                    {record.hasActualMeasurements
                                      ? "View Actual Measurements"
                                      : "View Planned Entries"}
                                  </button>
                                </div>
                                {!record.hasActualMeasurements && (
                                  <div className="mb-4 flex items-center gap-2.5 p-3 bg-amber-50 border border-amber-100 rounded-xl text-xs text-amber-700">
                                    <TriangleAlert
                                      size={14}
                                      className="text-amber-500 shrink-0"
                                    />
                                    <span className="font-semibold">
                                      Actual product not yet recorded. Use{" "}
                                      <strong>Actual Measurements</strong> to
                                      complete this production.
                                    </span>
                                  </div>
                                )}
                                <div className="space-y-3">
                                  {record.entries &&
                                  record.entries.length > 0 ? (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                      {record.entries.map((entry, idx) => {
                                        const c =
                                          colorMap[entry.color] ||
                                          colorMap.slate;
                                        const pct =
                                          record.totalActualWeight > 0
                                            ? Math.min(
                                                100,
                                                (parseFloat(entry.quantity) /
                                                  record.totalActualWeight) *
                                                  100,
                                              )
                                            : 0;
                                        return (
                                          <div
                                            key={idx}
                                            className={`flex flex-col p-3.5 rounded-xl border ${c.border} bg-white shadow-sm hover:shadow-md transition-shadow`}
                                          >
                                            <div className="flex justify-between items-start mb-1.5">
                                              <span
                                                className={`text-[10px] font-black uppercase tracking-wider ${c.text}`}
                                              >
                                                {entry.tagName}
                                              </span>
                                              <span
                                                className={`text-xs font-black ${c.text}`}
                                              >
                                                {fmtQty(
                                                  entry.quantity,
                                                  entry.unit,
                                                )}
                                              </span>
                                            </div>
                                            <div
                                              className="text-[10px] text-slate-500 font-semibold truncate mb-3"
                                              title={entry.productName}
                                            >
                                              {entry.productName}
                                            </div>
                                            <div className="mt-auto h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
                                              <div
                                                className={`h-full ${c.bg} transition-all duration-500`}
                                                style={{ width: `${pct}%` }}
                                              />
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  ) : (
                                    <p className="text-sm text-slate-400 italic bg-white p-5 rounded-xl border border-slate-200 text-center">
                                      No ingredients recorded in this batch.
                                    </p>
                                  )}
                                </div>
                              </div>
                              <div>
                                <h4 className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-4 flex items-center gap-2">
                                  <PackageCheck
                                    size={14}
                                    className="text-indigo-400"
                                  />{" "}
                                  Record Details
                                </h4>
                                <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-4">
                                  {[
                                    {
                                      icon: <Hash size={15} />,
                                      label: "Delivery Order",
                                      value: record.deliveryOrderNumber || "—",
                                    },
                                    {
                                      icon: <Warehouse size={15} />,
                                      label: "Warehouse Source",
                                      value: record.warehouseName || "—",
                                    },
                                    {
                                      icon: <Calendar size={15} />,
                                      label: "Production Date",
                                      value: formatDate(record.productionDate),
                                    },
                                    {
                                      icon: <User size={15} />,
                                      label: "Created By",
                                      value: record.createdBy || "—",
                                    },
                                  ].map((info) => (
                                    <div
                                      key={info.label}
                                      className="flex items-center justify-between border-b border-slate-50 last:border-0 pb-3 last:pb-0"
                                    >
                                      <div className="flex items-center gap-2.5 text-slate-400">
                                        {info.icon}{" "}
                                        <span className="text-[10px] font-black uppercase tracking-widest">
                                          {info.label}
                                        </span>
                                      </div>
                                      <span className="text-sm font-bold text-slate-800">
                                        {info.value}
                                      </span>
                                    </div>
                                  ))}
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2.5 text-slate-400">
                                      <Weight size={15} />
                                      <span className="text-[10px] font-black uppercase tracking-widest">
                                        Status
                                      </span>
                                    </div>
                                    <MeasurementBadge
                                      hasActualMeasurements={
                                        record.hasActualMeasurements
                                      }
                                    />
                                  </div>
                                </div>
                                {record.remarks && (
                                  <div className="mt-4 p-4 bg-amber-50 rounded-xl border border-amber-100 shadow-sm relative overflow-hidden">
                                    <p className="text-[10px] font-black uppercase tracking-widest text-amber-600 mb-1.5 relative z-10">
                                      Remarks & Notes
                                    </p>
                                    <p className="text-sm text-amber-900 font-medium relative z-10">
                                      {record.remarks}
                                    </p>
                                    <AlertCircle
                                      size={60}
                                      className="absolute -bottom-4 -right-2 text-amber-500 opacity-10"
                                    />
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                  {currentRecords.length === 0 && (
                    <tr>
                      <td colSpan="8" className="px-6 py-20 text-center">
                        <div className="flex flex-col items-center">
                          <div className="w-16 h-16 bg-slate-50 border border-slate-100 rounded-full flex items-center justify-center mb-4">
                            <AlertCircle size={28} className="text-slate-300" />
                          </div>
                          <p className="text-slate-700 font-bold text-lg">
                            No production records found
                          </p>
                          <p className="text-slate-500 text-sm mt-1">
                            {searchTerm
                              ? "Try adjusting your search filters."
                              : "Create your first formulated production record."}
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* MOBILE CARDS */}
            <div className="lg:hidden space-y-4">
              {currentRecords.map((record) => (
                <div
                  key={record.id}
                  className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden"
                >
                  <div
                    className="p-4 cursor-pointer"
                    onClick={() => toggleExpand(record.id)}
                  >
                    <div className="flex justify-between items-start gap-3 mb-2">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="p-2.5 bg-emerald-50 border border-emerald-100 rounded-xl text-emerald-600 shrink-0">
                          <FlaskConical size={18} />
                        </div>
                        <div className="min-w-0">
                          <h3 className="font-bold text-slate-900 truncate">
                            {record.productionNumber || `PRD-${record.id}`}
                          </h3>
                          <p className="text-[10px] font-bold text-indigo-600 truncate flex items-center gap-1.5 mt-0.5 tracking-wider uppercase">
                            <PackageCheck size={12} />{" "}
                            {record.deliveryOrderNumber || "—"}
                          </p>
                        </div>
                      </div>
                      <div className="text-right shrink-0 space-y-1">
                        <MeasurementBadge
                          hasActualMeasurements={record.hasActualMeasurements}
                        />
                        <p className="text-[10px] text-slate-400 font-semibold">
                          {formatDate(record.productionDate)}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1.5 mt-3 pt-3 border-t border-slate-50">
                      {record.entries?.map((e, i) => {
                        const c = colorMap[e.color] || colorMap.slate;
                        return (
                          <span
                            key={i}
                            className={`inline-flex items-center text-[9px] font-bold px-1.5 py-0.5 rounded border ${c.border} ${c.bgLight} ${c.text}`}
                          >
                            {e.tagName}
                          </span>
                        );
                      })}
                    </div>
                  </div>

                  {expandedId === record.id && (
                    <div className="border-t border-slate-100 bg-slate-50 p-4 space-y-3">
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        <button
                          onClick={() => setMeasurementsRecord(record)}
                          className={`py-2.5 rounded-xl text-xs font-bold border shadow-sm flex items-center justify-center gap-1.5 transition ${record.hasActualMeasurements ? "bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700" : "bg-white text-amber-600 border-amber-200 hover:bg-amber-50"}`}
                        >
                          <Eye size={13} />{" "}
                          {record.hasActualMeasurements
                            ? "View Actuals"
                            : "Entries"}
                        </button>
                        <button
                          onClick={() => triggerSinglePrint(record)}
                          className="py-2.5 bg-white text-blue-600 rounded-xl text-xs font-bold border border-blue-200 shadow-sm flex items-center justify-center gap-1.5 hover:bg-blue-50 transition"
                        >
                          <Printer size={14} /> Print
                        </button>
                        <button
                          onClick={() => handleEdit(record)}
                          className="py-2.5 bg-white text-amber-700 rounded-xl text-xs font-bold border border-amber-200 shadow-sm flex items-center justify-center gap-1.5 hover:bg-amber-50"
                        >
                          <Edit size={14} /> Edit
                        </button>
                        <button
                          onClick={() => handleSoftDelete(record.id)}
                          className="py-2.5 bg-white text-red-600 rounded-xl text-xs font-bold border border-red-200 shadow-sm flex items-center justify-center gap-1.5 hover:bg-red-50"
                        >
                          <Archive size={14} /> Archive
                        </button>
                      </div>
                      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
                        <div className="flex items-center gap-2 text-slate-400 mb-2">
                          <Warehouse size={14} />
                          <span className="text-[10px] font-black uppercase tracking-widest">
                            Warehouse
                          </span>
                        </div>
                        <p className="text-sm font-semibold text-slate-800">
                          {record.warehouseName || "—"}
                        </p>
                      </div>
                      {record.remarks && (
                        <div className="p-4 bg-amber-50 rounded-xl border border-amber-100 shadow-sm">
                          <p className="text-[10px] font-black uppercase tracking-widest text-amber-600 mb-1">
                            Remarks
                          </p>
                          <p className="text-xs font-medium text-amber-900">
                            {record.remarks}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
              {currentRecords.length === 0 && (
                <div className="text-center py-16 text-slate-400 bg-white rounded-2xl border border-slate-200 shadow-sm">
                  <AlertCircle
                    size={32}
                    className="mx-auto mb-3 text-slate-300"
                  />
                  <p className="font-bold text-slate-600 text-lg">
                    No records found
                  </p>
                </div>
              )}
            </div>

            <div className="mt-6">
              <Pagination
                itemsPerPage={itemsPerPage}
                totalItems={filteredData.length}
                currentPage={currentPage}
                paginate={(p) => setCurrentPage(p)}
              />
            </div>
          </>
        )}
      </div>

      {/* MODALS */}
      {isAddModalOpen && (
        <AddProduction
          onClose={() => setIsAddModalOpen(false)}
          refreshData={fetchData}
        />
      )}
      {isEditModalOpen && selectedToEdit && (
        <AddProduction
          productionData={selectedToEdit}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedToEdit(null);
          }}
          refreshData={fetchData}
        />
      )}
      <ProductionHistoryModal
        isOpen={isHistoryModalOpen}
        onClose={() => setIsHistoryModalOpen(false)}
        currentUserId={currentUserId}
        onRestored={fetchData}
      />
      <MeasurementsViewModal
        record={measurementsRecord}
        isOpen={!!measurementsRecord}
        onClose={() => setMeasurementsRecord(null)}
      />

      {/* HIDDEN PRINT TARGETS */}
      <div className="hidden">
        {printTarget && (
          <ProductionRecordPrint
            ref={singlePrintRef}
            data={printTarget.data}
            actualData={printTarget.actualData}
          />
        )}
        <ProductionListPrint
          ref={listPrintRef}
          dataList={filteredData}
          filters={`Search: ${searchTerm || "None"}`}
        />
      </div>
    </div>
  );
};

export default AllProduction;
